import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import React, { Fragment, useCallback } from 'react';
import { WrappedFieldProps } from 'redux-form';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { getThreshold } from 'dashboard/models/match-requirement-profile';
import { ComparisonMethod, ComparisonMethodType } from 'dashboard/models/verification-check';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';

const CONFIGURABLE_COMPARISON_METHOD_TYPES = [
  ComparisonMethodType.DateDifferenceDay,
  ComparisonMethodType.DateDifferenceMonth,
  ComparisonMethodType.DateDifferenceYear,
  ComparisonMethodType.DateSimilarity,
  ComparisonMethodType.DoingBusinessAs,
  ComparisonMethodType.Nickname,
  ComparisonMethodType.StringDifference,
  ComparisonMethodType.StringSimilarity,
  ComparisonMethodType.Substring,
  ComparisonMethodType.Tokenization,
];

const DEFAULT_COMPARISON_METHOD_SETTINGS: Record<
  Partial<ComparisonMethodType>,
  ComparisonMethod
> = {
  [ComparisonMethodType.DateDifferenceDay]: {
    type: ComparisonMethodType.DateDifferenceDay,
    'full-match-threshold': 0,
    'partial-match-threshold': 1,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.DateDifferenceMonth]: {
    type: ComparisonMethodType.DateDifferenceMonth,
    'full-match-threshold': 0,
    'partial-match-threshold': 1,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.DateDifferenceYear]: {
    type: ComparisonMethodType.DateDifferenceYear,
    'full-match-threshold': 0,
    'partial-match-threshold': 1,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.DateSimilarity]: {
    type: ComparisonMethodType.DateSimilarity,
    'full-match-threshold': 3,
    'partial-match-threshold': 2,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.DoingBusinessAs]: {
    type: ComparisonMethodType.DoingBusinessAs,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.Nickname]: {
    type: ComparisonMethodType.Nickname,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.StringDifference]: {
    type: ComparisonMethodType.StringDifference,
    'full-match-threshold': 0,
    'partial-match-threshold': 1,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.StringSimilarity]: {
    type: ComparisonMethodType.StringSimilarity,
    'full-match-threshold': 1,
    'partial-match-threshold': 1,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.Substring]: {
    type: ComparisonMethodType.Substring,
    'attribute-length-minimum': 3,
    'match-level-minimum': 'partial',
  },
  [ComparisonMethodType.Tokenization]: {
    type: ComparisonMethodType.Tokenization,
    'token-match-count-minimum': 1,
    'match-level-minimum': 'partial',
  },
  // Non-configurable comparison methods, if we want them to be configurable please add default settings
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.Address]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.HaversineDistance]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.LogicComposition]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.DamerauLevenshteinSimilarity]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.JaroWinklerSimilarity]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.StringDifferencePrefix]: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [ComparisonMethodType.StringDifferenceSuffix]: undefined,
};

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 15% 85%;
`;

const StyledParagraph = styled(Paragraph)`
  padding-top: 13px;
  padding-bottom: ${Spacing[2]};
  padding-left: ${Spacing[1]};
  font-size: 13px;
  line-height: ${Spacing[4]};
`;

const RulesContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[2]} ${Spacing[4]};
  display: flex;
  flex-direction: column;
`;

const RuleContainer = styled.div`
  padding-top: ${Spacing[2]};
`;

const FieldPill = styled.span`
  background-color: ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
  display: inline-flex;
  width: fit-content;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  ${paragraphMixins.sm};
  margin: 2px;
  padding: 4px;
`;

const InputLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const EditRulesSection = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const Grid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 130px 1fr 32px;
  column-gap: ${Spacing[2]};
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  width: ${Spacing[7]};
  height: ${Spacing[5]};
`;

const CenteredIcon = styled.div`
  display: block;
  margin-top: auto;
  margin-bottom: auto;
`;

const CenteredLabel = styled(Label)`
  text-align: center;
  margin: auto;
`;

interface ComparisonMethodsSectionProps {
  editable: boolean;
}
const ComparisonMethodsSection = ({
  editable,
  input,
}: ComparisonMethodsSectionProps & WrappedFieldProps) => {
  const comparisonMethods = input.value as ComparisonMethod[];
  const [
    showComparisonMethodEditor,
    { toggle: toggleComparisonMethodEditor, disable: closeComparisonMethodEditor },
  ] = useToggle(false);
  const handleAddComparisonMethod = useCallback(() => {
    const newComparisonMethods = produce(comparisonMethods, (draftComparisonMethods) => {
      draftComparisonMethods.push({
        'match-level-minimum': '',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        type: null,
      });
    });
    input.onChange(newComparisonMethods);
  }, [comparisonMethods, input]);
  const handleUpdateComparisonMethodType = useCallback(
    (index: number, type: ComparisonMethodType) => {
      const newComparisonMethods = produce(comparisonMethods, (draftComparisonMethods) => {
        draftComparisonMethods[index] = DEFAULT_COMPARISON_METHOD_SETTINGS[type];
      });
      input.onChange(newComparisonMethods);
    },
    [comparisonMethods, input],
  );
  // TODO: [PER-22576] Add tests to make sure this works for current and future comparison methods
  const handleUpdateComparisonMethodConfig = useCallback(
    (index: number, value: number) => {
      const newComparisonMethods = produce(comparisonMethods, (draftComparisonMethods) => {
        value = Math.max(Math.min(value, 100), 0);
        switch (draftComparisonMethods[index].type) {
          case ComparisonMethodType.Substring:
            draftComparisonMethods[index]['attribute-length-minimum'] = value;
            break;
          case ComparisonMethodType.Tokenization:
            draftComparisonMethods[index]['token-match-count-minimum'] = value;
            break;
          case ComparisonMethodType.StringSimilarity:
            draftComparisonMethods[index]['partial-match-threshold'] = Math.max(
              Math.min(+(value / 100).toPrecision(2), 1),
              0,
            );
            draftComparisonMethods[index]['match-level-minimum'] = 'partial';
            break;
          default:
            draftComparisonMethods[index]['partial-match-threshold'] = value;
            draftComparisonMethods[index]['match-level-minimum'] = 'partial';
            break;
        }
      });
      input.onChange(newComparisonMethods);
    },
    [comparisonMethods, input],
  );
  const handleDeleteComparisonMethod = useCallback(
    (index: number) => {
      const newComparisonMethods = produce(comparisonMethods, (draftComparisonMethods) => {
        draftComparisonMethods.splice(index, 1);
      });
      input.onChange(newComparisonMethods);
    },
    [comparisonMethods, input],
  );
  return (
    <Fragment>
      <HeadingContainer>
        <InputLabel>Comparison Methods</InputLabel>
        {editable && (
          <Button
            variant='basic'
            width='icon'
            type='button'
            id='match-requirement-panel__edit-comparison-methods'
            onClick={toggleComparisonMethodEditor}
          >
            <EditIcon />
          </Button>
        )}
      </HeadingContainer>
      <Spacer height={1} />
      <RulesContainer>
        {comparisonMethods.length > 0 ? (
          comparisonMethods.map((comparisonMethod, i) => (
            <GridRow key={i}>
              <StyledParagraph> {i === 0 ? 'Where' : 'Or'}</StyledParagraph>
              <RuleContainer>
                <ComparisonMethodConfig comparisonMethod={comparisonMethod} />
              </RuleContainer>
            </GridRow>
          ))
        ) : (
          <StyledParagraph>
            {editable ? 'Press the edit button to add comparison methods' : 'No comparison methods'}
          </StyledParagraph>
        )}
      </RulesContainer>
      <Spacer height={1} />
      {editable && showComparisonMethodEditor && (
        <ComparisonMethodsEditor
          comparisonMethods={comparisonMethods}
          handleAddComparisonMethod={handleAddComparisonMethod}
          handleUpdateComparisonMethodType={handleUpdateComparisonMethodType}
          handleUpdateComparisonMethodConfig={handleUpdateComparisonMethodConfig}
          handleDeleteComparisonMethod={handleDeleteComparisonMethod}
          onClose={closeComparisonMethodEditor}
        />
      )}
    </Fragment>
  );
};

interface ComparisonMethodConfigProps {
  comparisonMethod: ComparisonMethod;
}
const ComparisonMethodConfig = ({ comparisonMethod }: ComparisonMethodConfigProps) => {
  const threshold = getThreshold(comparisonMethod);
  switch (comparisonMethod.type) {
    // Comparison method type would be null when we are adding a new comparison method
    case null:
      return <FieldPill> empty method</FieldPill>;
    case ComparisonMethodType.Nickname:
      return <FieldPill> {comparisonMethod.type}</FieldPill>;
    case ComparisonMethodType.DoingBusinessAs:
      return <FieldPill> {comparisonMethod.type}</FieldPill>;
    case ComparisonMethodType.StringSimilarity:
      return (
        <>
          <FieldPill> {comparisonMethod.type}</FieldPill>
          <FieldPill> meets threshold of </FieldPill>
          <FieldPill> {threshold}%</FieldPill>
        </>
      );
    default:
      return (
        <>
          <FieldPill> {comparisonMethod.type}</FieldPill>
          <FieldPill> meets threshold of </FieldPill>
          <FieldPill> {threshold}</FieldPill>
        </>
      );
  }
};

interface ComparisonMethodsEditorProps {
  comparisonMethods: ComparisonMethod[];
  handleAddComparisonMethod: () => void;
  handleUpdateComparisonMethodType: (index: number, type: ComparisonMethodType) => void;
  handleUpdateComparisonMethodConfig: (index: number, value: number) => void;
  handleDeleteComparisonMethod: (index: number) => void;
  onClose: () => void;
}
const ComparisonMethodsEditor = ({
  comparisonMethods,
  handleAddComparisonMethod,
  handleUpdateComparisonMethodType,
  handleUpdateComparisonMethodConfig,
  handleDeleteComparisonMethod,
  onClose,
}: ComparisonMethodsEditorProps) => {
  return (
    <MaximizablePanel
      title='Edit Comparison Methods'
      onOutsideClickAndEscPress={onClose}
      offsetElementId='match-requirement-panel__container'
    >
      <EditRulesSection>
        {comparisonMethods.length > 0 ? (
          comparisonMethods.map((comparisonMethod, i) => (
            <React.Fragment key={i}>
              <Spacer height={1} />
              <Label size='sm'>{i === 0 ? 'Where' : 'Or'}</Label>
              <Spacer height={1} />
              <Grid key={i}>
                <Select
                  value={comparisonMethod.type}
                  onValueChange={(type) =>
                    handleUpdateComparisonMethodType(i, type as ComparisonMethodType)
                  }
                  placeholder='Select type'
                >
                  {CONFIGURABLE_COMPARISON_METHOD_TYPES.map((comparisonMethodType) => (
                    <Option key={comparisonMethodType} value={comparisonMethodType}>
                      {comparisonMethodType}
                    </Option>
                  ))}
                </Select>
                {comparisonMethod.type && (
                  <ComparisonMethodsConfigEditor
                    comparisonMethod={comparisonMethod}
                    handleUpdateComparisonMethodConfig={(value) =>
                      handleUpdateComparisonMethodConfig(i, value)
                    }
                  />
                )}
                <CenteredIcon>
                  <Button
                    id='multi-input__value-remove'
                    variant='basic'
                    width='icon'
                    onClick={() => handleDeleteComparisonMethod(i)}
                  >
                    <TrashIcon />
                  </Button>
                </CenteredIcon>
              </Grid>
            </React.Fragment>
          ))
        ) : (
          <StyledParagraph> Press &quot;Or&quot; to add a comparison method</StyledParagraph>
        )}
        <Spacer height={2} />
        <StyledButton
          onClick={handleAddComparisonMethod}
          id='match-requirement-panel__add-comparison-method'
          type='button'
          variant='basic'
          width='icon'
        >
          Or
        </StyledButton>
      </EditRulesSection>
    </MaximizablePanel>
  );
};

interface ComparisonMethodsConfigEditorProps {
  comparisonMethod: ComparisonMethod;
  handleUpdateComparisonMethodConfig: (value: number) => void;
}
const ComparisonMethodsConfigEditor = ({
  comparisonMethod,
  handleUpdateComparisonMethodConfig,
}: ComparisonMethodsConfigEditorProps) => {
  const threshold = getThreshold(comparisonMethod);
  switch (comparisonMethod.type) {
    case ComparisonMethodType.Nickname:
      return <></>;
    case ComparisonMethodType.DoingBusinessAs:
      return <></>;
    default:
      return (
        <>
          <CenteredLabel>meets threshold of</CenteredLabel>
          <Input
            min='0'
            max='100'
            type='number'
            // Trick to remove leading zeros from input
            value={threshold?.toString()}
            onChange={(e) => handleUpdateComparisonMethodConfig(+e.target.value)}
            placeholder='threshold'
          />
        </>
      );
  }
};

export default ComparisonMethodsSection;
